import {
  CustomPaging,
  IntegratedPaging,
  IntegratedSelection,
  IntegratedSorting,
  PagingPanel,
  PagingState,
  SelectionState,
  SortingState,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableColumnVisibility,
  TableHeaderRow,
  TableSelection,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import { CircularProgress, Tooltip, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { GridRoot } from './components/GridRoot';
import { PagingPanelContainer } from './components/PagingPanelContainer';
import TableActionLoading from './components/TableActionLoading';
import TableSelectionCell from './components/TableSelectionCell';
import TableSelectionHeaderCell from './components/TableSelectionHeaderCell';
import ptBr from './i18n/pt-br';
import { GridContainer } from './styles';

const DEFAULT_EMPTY_CELL_VALUE = '-';

const getRowId = (row) => row?.id || row?.key;

const useStyles = makeStyles((theme) => ({
  tableRow: {
    '&:hover, &:focus': {
      cursor: 'pointer',
      backgroundColor: theme.palette.grey[100],
    },
    '&:active': {
      cursor: 'pointer',
      backgroundColor: theme.palette.grey[50],
    },
  },
  tableCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: '0px !important',
  },
  pagingTextPanel: {
    fontSize: '1.2rem',
    fontFamily: 'Muli,Roboto,"Helvetica",Arial,sans-serif',
    fontWeight: '400',
    lineHeight: '1.66',
  },
  headerCell: {
    '&>span': {
      flexDirection: 'row',
    },
  },
}));

function RowComponent(onRowClick) {
  const classes = useStyles();
  return (props) => {
    return (
      <VirtualTable.Row
        {...props}
        onClick={(event) => onRowClick && onRowClick(props.row, event)}
        className={clsx(classes.tableRow)}
      />
    );
  };
}

const noDataCellComponent = (loading) => {
  return (props) => {
    if (loading) {
      return (
        <td
          style={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            top: '53.48px', // material row height
            left: 0,
            bottom: 0,
            right: 0,
          }}
        >
          <div>
            <CircularProgress color="primary" />
          </div>
          <div>Por favor aguarde, carregando dados...</div>
        </td>
      );
    }
    return <VirtualTable.NoDataCell {...props} />;
  };
};

const PagingPanelContainerComponent = (selection, selectionType, footerTable, hideSelection) => {
  return (props) => (
    <>
      {footerTable}
      <PagingPanelContainer
        {...props}
        hideSelection={hideSelection}
        selection={selection}
        selectionType={selectionType}
        className={'stepRodape'}
      />
    </>
  );
};

function SimpleVirtualTable({ modal, onRowClick, showCellEmpty, loading, tableColumnExtensions }) {
  const classes = useStyles();
  return (
    <VirtualTable
      style={{ marginBottom: 0 }}
      // tableComponent={<table />}
      columnExtensions={tableColumnExtensions}
      height={modal ? undefined : 'auto'}
      rowComponent={RowComponent(onRowClick)}
      cellComponent={(props) => {
        // if (
        //   showCellEmpty === false &&
        //   (!props.column.getCellValue || !props.column.getCellValue(props.row)) &&
        //   (props.value === undefined || props.value === null)
        // ) {
        //   props.column.getCellValue = () => {
        //     return props.column.empty !== undefined ? props.column.empty : DEFAULT_EMPTY_CELL_VALUE;
        //   };
        // }
        if (props.column.cellComponent) {
          const { cellComponent: CellComponent } = props.column;

          return (
            <VirtualTable.Cell {...props}>
              <CellComponent {...props} />
            </VirtualTable.Cell>
          );
        }
        if (props.column.name === '' || props.column.name === undefined) {
          return <VirtualTable.Cell {...props} />;
        }

        let value = props.column.getCellValue ? props.column.getCellValue(props.row) : props.value;

        if ((value === null || value === undefined) && !showCellEmpty) {
          value = DEFAULT_EMPTY_CELL_VALUE;
        }

        const tooltip = props.column.getTooltip ? props.column.getTooltip(props.row) : props.value;
        const hideTooltip = props.column.hideTooltip || !value || value === '-';

        const hideTooltipFunction = props.column.hideTooltipFunction
          ? props.column.hideTooltipFunction(props.row)
          : false;

        return (
          <VirtualTable.Cell {...props}>
            {hideTooltip || hideTooltipFunction ? (
              <div className={clsx(classes.tableCell)}>{value}</div>
            ) : (
              <Tooltip title={tooltip}>
                <div className={clsx(classes.tableCell)}>{value}</div>
              </Tooltip>
            )}
          </VirtualTable.Cell>
        );
      }}
      noDataCellComponent={noDataCellComponent(loading)}
      messages={ptBr.table.tableMessages}
    />
  );
}

function SimpleTableSelection({
  rows,
  pageSize,
  loading,
  selection,
  totalCount,
  onSelectionChange,
  selectionType,
  onSelectionTypeChange,
  hideTopSelection,
}) {
  return (
    <TableSelection
      showSelectAll={!hideTopSelection}
      headerCellComponent={(props) => (
        <TableSelectionHeaderCell
          {...props}
          rows={rows}
          pageSize={pageSize}
          loading={loading}
          selection={selection}
          totalCount={totalCount}
          onSelectionChange={onSelectionChange}
          selectionType={selectionType}
          onSelectionTypeChange={onSelectionTypeChange}
        />
      )}
      cellComponent={(props) => (
        <TableSelectionCell
          {...props}
          pageSize={pageSize}
          selection={selection}
          totalCount={totalCount}
          onSelectionChange={onSelectionChange}
          selectionType={selectionType}
          onSelectionTypeChange={onSelectionTypeChange}
        />
      )}
    />
  );
}

const GridRootBase = ({ children, ...props }) => (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }} {...props}>
    {children}
  </div>
);

export function SimpleTableBase({
  columns,
  rows,

  fetchData,
  fetchOnCreate,
  totalCount,

  searchBy,

  defaultPageSizes,

  defaultCurrentPage,
  defaultPageSize,

  defaultSorting,

  onCurrentPageChange,
  onPageSizeChange,

  onSortingChange,
  onRowClick,

  loading,

  pageType,

  showCellEmpty,

  selection,
  onSelectionChange,
  selectionType,
  onSelectionTypeChange,
  footerTable,

  params = {},

  hideSelection,
  hideTopSelection,

  rootComponent = GridRootBase,
  hiddenColumnNames = [],
  autoHeight = false,
  contemFiltroAvancado,
  hidePagination,
}) {
  const [sorting, setSorting] = useState(defaultSorting);
  const [currentPage, setCurrentPage] = useState(defaultCurrentPage);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageSizes] = useState(defaultPageSizes);
  const [tableInitialized, setTableInitialized] = useState(false);

  const [columnExtensions] = useState(
    columns
      // .filter((col) => col.sortingEnabled === false)
      .map((col) => {
        return { ...col, columnName: col.name };
      })
  );

  useEffect(() => {
    if (contemFiltroAvancado) {
      setCurrentPage(0);
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!defaultSorting.length) {
      setSorting(getDefaultSorting());
    }
    if (
      pageType === 'async' &&
      !loading &&
      fetchOnCreate &&
      (rows === undefined || (Array.isArray(rows) && !rows.length))
    ) {
      fetchData({
        page: currentPage,
        size: pageSize,
        sort: getSort(sorting),
        q: searchBy,
      });
    }
    setTableInitialized(true);
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && tableInitialized) {
      fetchData({
        page: 0,
        size: pageSize,
        sort: getSort(sorting),
        q: searchBy || '',
        ...params,
      });
    }

    setCurrentPage(0);
    onCurrentPageChange(0);

    if (selectionType !== 'NENHUM') {
      onSelectionTypeChange('NENHUM');
    }
    if (selection && selection.length) {
      onSelectionChange([]);
    }
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy]);

  const getDefaultSorting = () => {
    return [{ columnName: columns[0].name, direction: 'asc' }];
  };

  const getSort = (props) => {
    return props.map((item) => `${item.columnName},${item.direction}`).toString();
  };

  const styles = useStyles();

  return (
    <>
      <Grid getRowId={getRowId} rows={rows} columns={columns} rootComponent={rootComponent}>
        <SelectionState selection={selection.map(getRowId)} />
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={(props) => {
            setCurrentPage(props);
            if (pageType === 'async') {
              fetchData({
                page: props,
                size: pageSize,
                sort: getSort(sorting),
                q: searchBy,
              });
            }
            onCurrentPageChange(props);
          }}
          pageSize={pageSize}
          onPageSizeChange={(props) => {
            setPageSize(props);
            if (pageType === 'async') {
              setCurrentPage(0);
              fetchData({
                page: 0,
                size: props,
                sort: getSort(sorting),
                q: searchBy,
              });
            }
            onPageSizeChange(props);
          }}
        />
        {pageType === 'async' && <CustomPaging totalCount={totalCount} />}

        <SortingState
          sorting={sorting}
          onSortingChange={(props) => {
            if (rows.length) {
              setSorting(props);
              if (selectionType !== 'NENHUM') {
                onSelectionTypeChange('NENHUM');
              }
              if (selection && selection.length) {
                onSelectionChange([]);
              }

              if (pageType === 'async') {
                fetchData({
                  page: currentPage,
                  size: pageSize,
                  sort: getSort(props),
                  q: searchBy,
                });
              }
              onSortingChange(getSort(props));
            }
          }}
          columnExtensions={columnExtensions}
        />

        <IntegratedSelection />
        {pageType === 'local' && <IntegratedPaging />}
        {pageType === 'local' && <IntegratedSorting />}

        <SimpleVirtualTable
          modal={!autoHeight}
          tableColumnExtensions={columnExtensions}
          onRowClick={onRowClick}
          showCellEmpty={showCellEmpty}
          loading={loading}
        />

        <TableHeaderRow
          showSortingControls
          messages={ptBr.table.sortingMessages}
          sortLabelComponent={(props) => {
            const { onSort, getMessage, ..._props } = props;
            return !props?.column?.sortingEnabled ? (
              <TableHeaderRow.Content {..._props} />
            ) : (
              <div className="stepIconeOrdenacaoDefault">
                <TableHeaderRow.SortLabel
                  {...props}
                  disabled={props.disabled || loading}
                  getMessage={() => {
                    return props.column.tooltipTitle ? props.column.tooltipTitle : props.column?.title;
                  }}
                  className={styles.headerCell}
                  direction={props?.direction ? (props.direction === 'asc' ? 'desc' : 'asc') : null}
                />
              </div>
            );
          }}
        />

        <TableColumnVisibility hiddenColumnNames={hiddenColumnNames} />
        {!hideSelection && (
          <SimpleTableSelection
            rows={rows}
            pageSize={pageSize}
            loading={loading}
            selection={selection}
            totalCount={totalCount}
            onSelectionChange={onSelectionChange}
            selectionType={selectionType}
            hideTopSelection={hideTopSelection}
            onSelectionTypeChange={onSelectionTypeChange}
          />
        )}
        {!hidePagination && (
          <PagingPanel
            containerComponent={PagingPanelContainerComponent(selection, selectionType, footerTable, hideSelection)}
            messages={ptBr.table.pagingPanelMessages}
            pageSizes={pageSizes}
          />
        )}
      </Grid>
      {rows && !!rows.length && loading && <TableActionLoading />}
    </>
  );
}
function SimpleTable({
  columns,
  rows,

  fetchData,
  fetchOnCreate,
  totalCount,

  searchBy,

  defaultPageSizes,

  defaultCurrentPage,
  defaultPageSize,

  defaultSorting,

  onCurrentPageChange,
  onPageSizeChange,

  onSortingChange,
  onRowClick,

  loading,

  pageType,

  showCellEmpty,

  params = {},
  hideSelection,

  hideTopSelection,
  selection,
  onSelectionChange,
  selectionType,
  onSelectionTypeChange,
  footerTable = <div />,
  hiddenColumnNames,
  contemFiltroAvancado,
  hidePagination,
}) {
  return (
    <GridContainer>
      <SimpleTableBase
        rootComponent={GridRoot}
        columns={columns}
        rows={rows}
        fetchData={fetchData}
        fetchOnCreate={fetchOnCreate}
        totalCount={totalCount}
        searchBy={searchBy}
        defaultPageSizes={defaultPageSizes}
        defaultCurrentPage={defaultCurrentPage}
        defaultPageSize={defaultPageSize}
        defaultSorting={defaultSorting}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        onRowClick={onRowClick}
        loading={loading}
        pageType={pageType}
        showCellEmpty={showCellEmpty}
        selection={selection}
        onSelectionChange={onSelectionChange}
        selectionType={selectionType}
        onSelectionTypeChange={onSelectionTypeChange}
        footerTable={footerTable}
        params={params}
        hideTopSelection={hideTopSelection}
        hideSelection={hideSelection}
        hiddenColumnNames={hiddenColumnNames}
        contemFiltroAvancado={contemFiltroAvancado}
        hidePagination={hidePagination}
      />
      {rows && !!rows.length && loading && <TableActionLoading />}
    </GridContainer>
  );
}

SimpleTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  fetchData: PropTypes.func,
  fetchOnCreate: PropTypes.bool,
  totalCount: PropTypes.number,
  searchBy: PropTypes.string,
  loading: PropTypes.bool,
  defaultSorting: PropTypes.arrayOf(PropTypes.any),
  defaultCurrentPage: PropTypes.number,
  defaultPageSize: PropTypes.number,
  defaultPageSizes: PropTypes.arrayOf(PropTypes.number),
  onSortingChange: PropTypes.func,
  onCurrentPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,

  pageType: PropTypes.oneOf(['async', 'local']),
  showCellEmpty: PropTypes.bool,
  selection: PropTypes.array,
  onSelectionChange: PropTypes.func,
  selectionType: PropTypes.oneOf(['NENHUM', 'TODOS_PAGINA', 'TODOS_FILTRO']),
  onSelectionTypeChange: PropTypes.func,
};

SimpleTableBase.defaultProps = {
  rows: [],
  columns: [],
  fetchData: () => {},
  fetchOnCreate: false,
  totalCount: 0,
  searchBy: undefined,
  loading: false,
  defaultSorting: [],
  defaultCurrentPage: 0,
  defaultPageSize: 15,
  defaultPageSizes: [15, 30, 60],
  onSortingChange: () => {},
  onCurrentPageChange: () => {},
  onPageSizeChange: () => {},

  pageType: 'async',
  showCellEmpty: false,
  selection: [],
  onSelectionChange: () => {},
  selectionType: 'NENHUM',
  onSelectionTypeChange: () => {},
  hidePagination: false,
};

SimpleTableBase.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  fetchData: PropTypes.func,
  fetchOnCreate: PropTypes.bool,
  totalCount: PropTypes.number,
  searchBy: PropTypes.string,
  loading: PropTypes.bool,
  defaultSorting: PropTypes.arrayOf(PropTypes.any),
  defaultCurrentPage: PropTypes.number,
  defaultPageSize: PropTypes.number,
  defaultPageSizes: PropTypes.arrayOf(PropTypes.number),
  onSortingChange: PropTypes.func,
  onCurrentPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,

  pageType: PropTypes.oneOf(['async', 'local']),
  showCellEmpty: PropTypes.bool,
  selection: PropTypes.array,
  onSelectionChange: PropTypes.func,
  selectionType: PropTypes.oneOf(['NENHUM', 'TODOS_PAGINA', 'TODOS_FILTRO']),
  onSelectionTypeChange: PropTypes.func,
  hidePagination: PropTypes.bool,
};
export default SimpleTable;
